import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ContactAgentModal from 'components/modal/ContactAgentModal';
import useWhiteLabelSettings from 'hooks/useWhiteLabelSettings';
import { setSelectedBooking, setSelectedQuote, setCanvasQuoteDocuments } from 'reducers/ui/uiSlice';
import { BUTTON_TYPE_LINK } from 'shared/constants/common';
import { ROUTES_SELF_SERVICE } from 'shared/constants/selfServiceRoutes';
import { getCanvasQuoteDocuments } from 'services/CommonService';
import { DEFAULT_PRIMARY_COLOUR } from 'shared/constants/styleConstants';
import {
  displayAmount,
  displayDate,
  getPluralForm,
} from 'shared/helpers/common';
import {
  getQuoteBookingComponentOverview,
  getPassengersToDisplay,
} from 'shared/helpers/quoteBooking';
import Button from '../Button';
import ItemDetails from '../ItemDetails';

export default function QuoteBookingCard({ isBooking, item }) {
  const { componentConfig, canvasQuoteDocuments } = useSelector((state) => state.ui);
  const { whiteLabelSettings: { primaryColour } = {} } =
    useWhiteLabelSettings();
  const backgroundStyle = {
    backgroundColor: primaryColour || DEFAULT_PRIMARY_COLOUR,
  };
  const [isLoyaltyBooking, setIsLoyaltyBooking] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const {
    id,
    reference,
    created,
    currency,
    currentPrice,
    price,
    passengerSummary,
    author = {},
    due,
    balanceDueDate,
  } = item;
  const viewButtonLabel = isBooking ? 'View booking' : 'View quote';
  const {
    name: componentName,
    dateToDisplay,
    startDateDiffToNow,
    componentType,
    statusLabel,
  } = getQuoteBookingComponentOverview(item);
  const passengerDisplay = getPassengersToDisplay(passengerSummary);

  const dispatch = useDispatch();

  const selectItem = () => {
    if (isBooking) {
      dispatch(setSelectedBooking(item));
    } else {
      dispatch(setSelectedQuote(item));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (componentConfig.showQuoteDocuments && Object.keys(canvasQuoteDocuments).length === 0) {
        try {
          const { data: canvasQuotesData } = await getCanvasQuoteDocuments();
          dispatch(setCanvasQuoteDocuments(canvasQuotesData || {}));
        } catch (error) {
          console.error('Error fetching canvasQuoteDocuments', error);
        }
      }
    };

    fetchData();
  }, [componentConfig.showQuoteDocuments, canvasQuoteDocuments, dispatch]);

  useEffect(() => {
    const foundLoyalty = item.tours.some(tour => tour.loyalty === true);
    setIsLoyaltyBooking(foundLoyalty);
  }, [item]);

  const isCanvasQuote = !isBooking && canvasQuoteDocuments && reference && canvasQuoteDocuments[reference];
  const getDetailsPageUrl = () => {
    if (isBooking) {
      return `${ROUTES_SELF_SERVICE.BOOKINGS}/${id || reference}`;
    }
    return isCanvasQuote
      ? canvasQuoteDocuments[reference]
      : `${ROUTES_SELF_SERVICE.QUOTES}/${id || reference}`;
  };

  const openModalHandler = () => {
    setOpenModal(!openModal);
  };

  return (
    <div className="mx-4">
      <div className="bg-white rounded-[10px] overflow-hidden mt-[40px] flex flex-col text-[18px]">
        {isBooking && (
          <div
            style={backgroundStyle}
            className="p-4 rounded-t-[10px] flex justify-center item-center gap-2 text-white text-xl font-bold"
          >
            {(isLoyaltyBooking && <div>Bundle</div>) ||
              (statusLabel && <div>{statusLabel}</div>) ||
              (startDateDiffToNow >= 0 && (
                <div>
                  {startDateDiffToNow} {getPluralForm('day', startDateDiffToNow)} to go
                </div>
              )) || 
            <div className="p-3" />}
          </div>
        )}
        <div className="bg-white p-4 w-full">
          <ItemDetails
            componentType={componentType}
            componentName={componentName}
            date={dateToDisplay}
            guest={passengerDisplay}
            totalPrice={displayAmount(currentPrice || price, currency)}
            due={displayAmount(due, currency)}
            balanceDueDate={displayDate(balanceDueDate, '-', undefined, true)}
            reference={reference}
            isLoyaltyBooking={isLoyaltyBooking}
          />
          <div className="mt-4 text-gray-500 text-[0.9rem] font-medium lg:flex lg:flex-row">
            <div className="mt-1 lg:mt-0">
              {isBooking ? 'Booked:' : 'Created:'}{' '}
              {displayDate(created, '-', 'dd/MM/yyyy', true)}
            </div>
          </div>
        </div>

        <div className="bg-white flex flex-col border-t rounded-b-[10px] md:flex-row">
          {componentConfig.contactAgentEnabled && (
            <Button
              label="Message Customer Services"
              secondary
              className="mt-3 mx-3 md:my-4 md:w-[50%] m-[10px] px-[15px] xl:!text-[13px]"
              onClick={openModalHandler}
            />
          )}
          <Button
            label={viewButtonLabel}
            className="grow md:my-4 md:ml-0 md:w-[50%] m-[10px] px-[15px]"
            type={BUTTON_TYPE_LINK}
            onClick={selectItem}
            url={getDetailsPageUrl()}
            isNewTab={!!isCanvasQuote}
          />
        </div>
        <ContactAgentModal
          assignee={author}
          reference={reference}
          showReference
          openModal={openModal}
          setOpenModal={setOpenModal}
          context={isBooking ? "booking" : "quote"}
        />
      </div>
    </div>
  );
}
